import type { QueryClient } from "@tanstack/react-query";
import type { TRPCLink } from "@trpc/client";
import type { AnyRouter } from "@trpc/server";

import * as React from "react";

import { getTokenFromStorage } from "@editor/infra/auth";
import { defaultFetchCredentials, getPublisherUrl } from "@editor/utils/env";

import {
  httpLink,
  httpSubscriptionLink,
  loggerLink,
  splitLink,
} from "@trpc/client";
import {
  createTRPCClient,
  createTRPCQueryUtils,
  createTRPCReact,
} from "@trpc/react-query";
import { createTRPCOptionsProxy } from "@trpc/tanstack-react-query";
import superjson from "superjson";

const transformer = superjson;

export function createTrpcRouterConfig<TRouter extends AnyRouter>(args: {
  path: string;
  queryClient: QueryClient;
}) {
  const { path, queryClient } = args;

  const router = createTRPCReact<TRouter>({
    context: React.createContext(null),
  });

  const baseHttpUrl = `${getPublisherUrl()}/api/v1/trpc${path ? `/${path}` : ""}`;

  const http = httpLink({
    url: baseHttpUrl,
    headers: () => ({
      "Cache-Control": "no-cache",
      Authorization: `Token ${getTokenFromStorage()}`,
    }),
    transformer,
    fetch(url, options) {
      return fetch(url, {
        ...options,
        credentials: defaultFetchCredentials,
      });
    },
  });

  const subscriptionLink = httpSubscriptionLink({
    url: async () => {
      const baseUrl = `${getPublisherUrl()}/api/v1/trpc${path ? `/${path}` : ""}`;
      const response = await fetch(
        `${getPublisherUrl()}/api/v1/trpc/user.fetchSubscriptionToken`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Token ${getTokenFromStorage()}`,
          },
        },
      );
      if (!response.ok) {
        throw new Error("Failed to fetch subscription token");
      }
      const { result } = await response.json();
      const subscriptionToken = result.data.json.subscriptionToken;
      return `${baseUrl}?subToken=${encodeURIComponent(subscriptionToken)}`;
    },
    transformer,
  });

  const finalLinks: TRPCLink<TRouter>[] = [
    loggerLink(),
    splitLink({
      condition: (op) => op.type === "subscription",
      true: subscriptionLink,
      false: http,
    }),
  ];

  const client = createTRPCClient<TRouter>({ links: finalLinks });

  const options = createTRPCOptionsProxy<TRouter>({
    client: client,
    queryClient,
  });

  const utils = createTRPCQueryUtils({
    queryClient,
    client,
  });

  return { router, client, utils, options } as const;
}
